import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import MHeader from "@/components/ui/organisms/MHeader";
import MLabParameterCard from "@/components/ui/atoms/MLabParameterCard";
import MLoader from "@/components/ui/atoms/MLoader";
import { useLabInsightsGroupDetails } from "@/hooks/useLabInsights";
import { getMemberInfo } from "@/hooks/useMembers";
import { useScrollRestore } from "@/hooks/useScrollRestore";
import { popPathSegment } from "@/utils/path-utils";
import { formatLabCardDate } from "@/utils/date-utils";
import {
  GroupParameterCard,
  LabParameterGroupId,
  ParameterStatus,
} from "@/types/lab-insights";

import styles from "./LabInsightsGroupDetails.module.css";

const isFlaggedReading = (param: GroupParameterCard) =>
  Boolean(param.latestReading?.flagged);

const formatValue = (
  param: GroupParameterCard
): { value: string | number | null; unit: string | null } => {
  const reading = param.latestReading;
  if (!reading) return { value: null, unit: null };
  if (reading.parsedResult !== null && reading.parsedResult !== undefined) {
    return { value: reading.parsedResult, unit: reading.unit };
  }
  return { value: reading.rawResult, unit: reading.unit };
};

const LabInsightsGroupDetails: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const location = useLocation();
  const { groupId: rawGroupId } = useParams<{ groupId: string }>();
  const groupId = (rawGroupId as LabParameterGroupId) ?? null;

  const { data, loading, error } = useLabInsightsGroupDetails(
    memberInfo?.id,
    groupId
  );

  // Preserve scroll position when drilling into a parameter's detail page and
  // coming back. Keyed per member + group so each group's card list restores
  // to its own spot (the back nav remounts this page fresh).
  //
  // The "still loading" arg MUST start true and flip false only once the
  // cards are in the DOM — same contract the documents listing follows
  // (HealthMemoryList passes `!initialLoadDone || isLoading`). This hook's
  // `loading` initialises to FALSE and only flips true once fetch starts,
  // so gating on `loading` alone lets useScrollRestore run its one-shot
  // restore on the very first mount, against an empty page (scrollTop clamps
  // to 0) — and it discards the saved value before the cards mount. Adding
  // `!data` holds the restore until the readings have actually loaded.
  const { saveScroll } = useScrollRestore(
    `lab-group-${memberInfo?.id ?? "unknown"}-${groupId ?? "none"}`,
    loading || !data
  );

  // Auto-center the selected tab in the horizontal tab strip so users don't
  // have to swipe to find the active group when the strip overflows.
  //
  // We scroll the STRIP's own scrollLeft rather than calling
  // element.scrollIntoView(): scrollIntoView scrolls EVERY scrollable
  // ancestor — including the page's `main` container vertically — to bring
  // the tab into view. With the tab strip at the top of the page, its
  // smooth animation can yank `main` back toward the top right as the
  // scroll-restore is trying to land, fighting the restore. Constraining
  // the scroll to the strip's horizontal axis leaves `main`'s vertical
  // scroll untouched so the restore is never disturbed.
  const selectedTabRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    const tab = selectedTabRef.current;
    const strip = tab?.parentElement;
    if (!tab || !strip) return;
    const stripRect = strip.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const delta =
      tabRect.left - stripRect.left - (strip.clientWidth - tab.clientWidth) / 2;
    strip.scrollTo({
      left: Math.max(0, strip.scrollLeft + delta),
      behavior: "smooth",
    });
  }, [groupId, data?.tabs?.length]);

  const handleBack = () => navigate(popPathSegment(location.pathname));

  const handleTabClick = (nextGroupId: LabParameterGroupId) => {
    if (nextGroupId === groupId) return;
    const basePath = location.pathname.includes("/groups/")
      ? "/lab-insights/groups"
      : "/lab-insights";
    navigate(`${basePath}/${nextGroupId}`);
  };

  const handleParameterClick = (param: GroupParameterCard) => {
    // Save before navigating so returning from the parameter detail page
    // restores this group's card-list scroll spot.
    saveScroll();
    if (param.canonicalParameterId) {
      // Matched row — Screen 3 keyed on the canonical id.
      navigate(`/lab-insights/parameters/${param.canonicalParameterId}`, {
        state: { from: location.pathname },
      });
      return;
    }
    // Unmatched OTHERS row (antibiotic sensitivities, organism, etc.) has no
    // canonical id. Drive Screen 3 by the raw OCR name instead — the `_raw`
    // sentinel path + raw/group query params hit the backend's raw-name
    // fallback. `param.canonicalName` holds the raw OCR name for unmatched
    // rows (the group-details endpoint falls back to rawParameterName there).
    navigate(
      `/lab-insights/parameters/_raw?raw=${encodeURIComponent(
        param.canonicalName
      )}&group=${encodeURIComponent(groupId ?? "OTHERS")}`,
      { state: { from: location.pathname } }
    );
  };

  const titleLabel =
    memberInfo?.nickname ||
    memberInfo?.firstName ||
    t("labInsights.title");

  const { outOfRange, withinRange } = useMemo(() => {
    const params = data?.parameters ?? [];
    return {
      outOfRange: params.filter(isFlaggedReading),
      withinRange: params.filter((p) => !isFlaggedReading(p)),
    };
  }, [data]);

  // Hide tabs for groups that have no data (active === false → "noResults"),
  // but always keep the currently-viewed group's tab so the page you're on
  // never loses its own tab.
  const visibleTabs = useMemo(
    () =>
      (data?.tabs ?? []).filter(
        (tab) => tab.active || tab.groupId === groupId,
      ),
    [data, groupId],
  );

  return (
    <div className={styles.page}>
      <MHeader
        module="labInsights"
        subHeading={titleLabel}
        memberInfo={memberInfo}
        hideLearnMore
        onBack={handleBack}
        className={styles.flushHeader}
      />

      {visibleTabs.length > 0 && (
        <div className={styles.tabsScroll}>
          {visibleTabs.map((tab) => {
            const isSelected = tab.groupId === groupId;
            return (
              <button
                key={tab.groupId}
                ref={isSelected ? selectedTabRef : undefined}
                type="button"
                className={[
                  styles.tab,
                  isSelected ? styles["tab--selected"] : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => handleTabClick(tab.groupId)}
              >
                {tab.groupName}
              </button>
            );
          })}
        </div>
      )}

      {loading && !data && <MLoader />}

      {error && !loading && (
        <p className={styles.emptyState}>{t("labInsights.errorLoading")}</p>
      )}

      {data && !loading && (
        <>
          {outOfRange.length > 0 && (
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <span
                  className={[
                    styles.sectionDot,
                    styles["sectionDot--outOfRange"],
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  aria-hidden
                />
                <span className={styles.sectionLabel}>
                  {t("labInsights.outOfRange")}
                </span>
              </div>
              <div className={styles.cardGrid}>
                {outOfRange.map((param, idx) => {
                  const { value, unit } = formatValue(param);
                  const reading = param.latestReading;
                  return (
                    <MLabParameterCard
                      key={
                        param.canonicalParameterId ??
                        `out-${param.canonicalName}-${idx}`
                      }
                      groupName={formatLabCardDate(reading?.reportDate ?? null)}
                      topLabelTone="indigo"
                      parameterName={param.canonicalName}
                      value={value}
                      unit={unit}
                      status={reading?.status ?? ParameterStatus.UNKNOWN}
                      flagged={reading?.flagged ?? false}
                      rangeLow={reading?.rangeLow ?? null}
                      rangeHigh={reading?.rangeHigh ?? null}
                      rangeText={reading?.rangeText ?? null}
                      showChevron
                      onCardClick={() => handleParameterClick(param)}
                    />
                  );
                })}
              </div>
            </section>
          )}

          {outOfRange.length > 0 && withinRange.length > 0 && (
            <hr className={styles.sectionDivider} />
          )}

          {withinRange.length > 0 && (
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <span
                  className={[
                    styles.sectionDot,
                    styles["sectionDot--withinRange"],
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  aria-hidden
                />
                <span className={styles.sectionLabel}>
                  {t("labInsights.withinRange")}
                </span>
              </div>
              <div className={styles.cardGrid}>
                {withinRange.map((param, idx) => {
                  const { value, unit } = formatValue(param);
                  const reading = param.latestReading;
                  return (
                    <MLabParameterCard
                      key={
                        param.canonicalParameterId ??
                        `in-${param.canonicalName}-${idx}`
                      }
                      groupName={formatLabCardDate(reading?.reportDate ?? null)}
                      topLabelTone="indigo"
                      parameterName={param.canonicalName}
                      value={value}
                      unit={unit}
                      status={reading?.status ?? ParameterStatus.UNKNOWN}
                      flagged={false}
                      rangeLow={reading?.rangeLow ?? null}
                      rangeHigh={reading?.rangeHigh ?? null}
                      rangeText={reading?.rangeText ?? null}
                      showChevron
                      onCardClick={() => handleParameterClick(param)}
                    />
                  );
                })}
              </div>
            </section>
          )}

          {outOfRange.length === 0 && withinRange.length === 0 && (
            <p className={styles.emptyState}>
              {t("labInsights.noReadingYet")}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default LabInsightsGroupDetails;
